/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery } from '@apollo/client';
import { Badge, Box, Button, Divider, Flex, SimpleGrid, Tab, TabList, TabPanel, TabPanels, Tabs, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { faFileAlt, faUserEdit } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UserDislayNameById from '../../../../components/UserDislayNameById';
import c from '../../../../constant';
import useStore from '../../../../store';
import { isAbleAllowEditing, isDataViewer } from '../../../../utils/market-development';
import CompanyName from '../../../CompanyName';
import LabelDate from '../../../LabelDate';
import { mdAnnuallyDueDate } from '../../../../utils/mdReportDueDate';
import moment from 'moment';
import Loading from '../../../Loading';
import EditAnnuallyReportModal from '../../../Modal/EditAnnuallyReportModal';
import { CompanyBadge } from '../../../Other';
import SpreadSheetKeyValuePair from '../../Miscellaneous/spreadsheet-key-value-pair';
import ExcelMicro from '../Excel/ExcelMicro';
import SpreadSheetTable from './table';
import EditHistoryModal from '../../Miscellaneous/edit-history-modal';
import useMoment from '../../../../hooks/useMoment';

const Q_DISTRIBUTION_ITEMS = gql`
	query get($id: String!) {
		getAnnuallyDistributionReportItems(id: $id) {
			id
			reportType
			year
			createdAt
			updatedAt
			company {
				id
				name
				nameKhmer
				logo
				type
			}
			isEditing
			editingUpdatedAt
			editingUpdatedBy
			editingDueDate
			editingDescription
			penalties {
				id
				active
			}
			distributionItems {
				id
				productLine {
					id
					nameKh
					nameEn
					type
				}
			}
		}
	}
`;

const Page = forwardRef(({ id = null }, ref) => {
	const toast = useToast();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { toDateString, toYear } = useMoment();
	const { currentUser: user } = useStore((state) => state?.currentUser);
	const [year, setYear] = useState(null);
	const [isEditing, setEditing] = useState(false);
	const [createdAt, setCreatedAt] = useState(null);
	const [updatedAt, setUpdatedAt] = useState(null);
	const [isPenalized, setPenalized] = useState(false);
	const [company, setCompany] = useState(null);
	const [distributionItems, setDistributionItems] = useState(null);
	const { isOpen: isHistoryOpen, onOpen: onHistoryOpen, onClose: onHistoryClose } = useDisclosure();
	const [getDetail, { data, loading, error }] = useLazyQuery(Q_DISTRIBUTION_ITEMS, { fetchPolicy: 'no-cache' });

	useEffect(loadDetail, [id]);

	useEffect(() => {
		if (data) {
			setYear(parseInt(data?.getAnnuallyDistributionReportItems?.year));
			setEditing(data?.getAnnuallyDistributionReportItems?.isEditing);
			setCreatedAt(data?.getAnnuallyDistributionReportItems?.createdAt);
			setUpdatedAt(data?.getAnnuallyDistributionReportItems?.updatedAt);
			setPenalized(data?.getAnnuallyDistributionReportItems?.penalties?.length > 0);
			setCompany(data?.getAnnuallyDistributionReportItems?.company);
			setDistributionItems(data?.getAnnuallyDistributionReportItems?.distributionItems);
		}
	}, [data]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	function loadDetail() {
		id && getDetail({ variables: { id } });
	}

	const dueDate = useMemo(() => mdAnnuallyDueDate({ year }), [year]);
	const isDueDateReached = useMemo(() => (dueDate ? moment().isSameOrAfter(dueDate, 'second') : false), [dueDate]);
	const canViewData = useMemo(() => isDataViewer({ user, companyType: 'Micro' }), [user]);
	const canAllowEditing = useMemo(() => canViewData && isAbleAllowEditing({ user, companyType: 'Micro' }), [user]);

	return (
		<>
			{loading ? (
				<>
					<Loading />
				</>
			) : (
				<>
					<Flex pl={4} pr={4} w="100%">
						<SimpleGrid columns={[1, null, 2]} w="100%" spacingX={4} spacingY={4}>
							<Flex w="100%" direction="column" border="1px dotted #dbdbdb" borderRadius={c.borderRadius} pl={4} pr={4} pt={4} pb={4}>
								<>
									{isPenalized && (
										<Box>
											<Badge colorScheme="red" variant="solid">
												{t('Penalized')}
											</Badge>
										</Box>
									)}
								</>
								<SpreadSheetKeyValuePair label="Company" value={<CompanyName company={company} />} />
								<SpreadSheetKeyValuePair label="Type" value={<CompanyBadge type={company?.type} />} />
								<SpreadSheetKeyValuePair label="Year" value={toYear(year)} />
								<SpreadSheetKeyValuePair label="Submitted At" value={<LabelDate input={createdAt} outputFormat={c.dateTimeFormat} />} />
								<>
									{updatedAt && (
										<>
											<SpreadSheetKeyValuePair
												label="Updated at"
												value={<LabelDate input={updatedAt} outputFormat={c.dateTimeFormat} />}
											/>
										</>
									)}
								</>
								{isDueDateReached && canViewData && isEditing && (
									<>
										<Divider variant="dashed" mt={2} mb={2} />
										<SpreadSheetKeyValuePair
											label="Allow Editing"
											value={
												<LabelDate input={data?.getAnnuallyDistributionReportItems?.editingUpdatedAt} outputFormat={c.dateTimeFormat} />
											}
										/>
										<SpreadSheetKeyValuePair
											label="By"
											value={<UserDislayNameById id={data?.getAnnuallyDistributionReportItems?.editingUpdatedBy} />}
										/>
										<SpreadSheetKeyValuePair
											label="Due Date"
											value={
												<LabelDate input={data?.getAnnuallyDistributionReportItems?.editingDueDate} outputFormat={c.dateTimeFormat} />
											}
										/>
										<SpreadSheetKeyValuePair label="Description" value={data?.getAnnuallyDistributionReportItems?.editingDescription} />
										{canAllowEditing && (
											<>
												<Box mt={2}>
													<Button
														onMouseDown={onOpen}
														isLoading={false}
														isDisabled={false}
														onClick={() => null}
														leftIcon={<FontAwesomeIcon icon={faUserEdit} style={{ fontSize: 16 }} />}
														colorScheme="red"
														variant="solid"
														size="sm"
														borderRadius={c.borderRadius}
														border="1px solid #bdc3c7"
														mr={2}
														width={lang === 'kh' ? '219px' : '195px'}
													>
														{t('Stop Allowing Editing')}
													</Button>
												</Box>
											</>
										)}
									</>
								)}
								<Flex mt={2}>
									{isDueDateReached && canAllowEditing && !isEditing && (
										<Button
											onMouseDown={onOpen}
											isLoading={false}
											isDisabled={false}
											onClick={() => null}
											leftIcon={<FontAwesomeIcon icon={faUserEdit} style={{ fontSize: 16 }} />}
											colorScheme="gray"
											variant="solid"
											size="sm"
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											mr={2}
										>
											{t('Allow Editing')}
										</Button>
									)}
									{canViewData && <ExcelMicro id={id} year={year} numColumns={21} />}
									{canViewData && (
										<Flex ml={2}>
											<Button
												colorScheme="gray"
												variant="solid"
												size="sm"
												borderRadius={c.borderRadius}
												border="1px solid #bdc3c7"
												leftIcon={<FontAwesomeIcon icon={faFileAlt} style={{ fontSize: 16 }} />}
												onClick={onHistoryOpen}
											>
												{t('View Logs')}
											</Button>
										</Flex>
									)}
								</Flex>
								{!isDueDateReached && canViewData && dueDate && (
									<Box h="100%" mt={2}>
										<Text fontSize="15px" fontStyle="italic" fontWeight="bold" justifyContent="center" color="orange.500">
											{t('Report can still be edited until the due date:')}&nbsp;
											{toDateString(dueDate, c.dateTimeFormat)}
										</Text>
									</Box>
								)}
							</Flex>
						</SimpleGrid>
					</Flex>
					<Box pl={4} pr={4} pb={4} pt={4} overflow="clip" hidden={canViewData ? false : true}>
						<Tabs isLazy variant="soft-rounded" colorScheme="green">
							<TabList overflow="scroll">
								{distributionItems?.map((el) => {
									return (
										<Tab key={el?.id}>
											<Text fontSize="small">{el?.productLine?.nameKh}</Text>
										</Tab>
									);
								})}
							</TabList>
							<TabPanels>
								{distributionItems?.map((el) => {
									return (
										<TabPanel key={el?.id} pl={0} pr={0} pb={0}>
											<>
												<SpreadSheetTable distributionItemId={el?.id} />
											</>
										</TabPanel>
									);
								})}
							</TabPanels>
						</Tabs>
					</Box>
				</>
			)}
			{isOpen && <EditAnnuallyReportModal id={id} type="Distribution" isEditable={isEditing} onClose={onClose} onCallback={loadDetail} />}
			{isHistoryOpen && <EditHistoryModal id={id} period="Yearly" category="Distribution" isOpen={isHistoryOpen} onClose={onHistoryClose} />}
		</>
	);
});

export default Page;
